import { EDIT_FIELDS } from '../pages/recordPages';

/**
 * 🔒 The change-of-data request an outside login files against its own company
 * (piece 21; decision 0971). The rules here are the ones the screen and the
 * route both read, so the window refuses what the route refuses, in the same
 * words.
 */

export const CHANGE_REQUEST_STATUSES = ['Open', 'Accepted', 'Declined'] as const;
export type ChangeRequestStatus = (typeof CHANGE_REQUEST_STATUSES)[number];

/**
 * 🔒 The Company form fields a request may name (piece 21, `ChangeRequest.field?`).
 * 🔨 Only the text fields: Accept prefills the named field with the request's
 * words, and a flag, a list or an amount cannot take a sentence.
 */
export const CHANGE_REQUEST_FIELDS = ['displayName', 'website', 'linkedInUrl', 'mainPhoneNumber', 'invoiceSubmissionNotes'] as const;
export type ChangeRequestField = (typeof CHANGE_REQUEST_FIELDS)[number];

/** The longest request a person may file, as the table's own check holds it. */
export const CHANGE_REQUEST_TEXT_MAX = 2000;

/** 🔒 The to-do filing raises for the account owner (decision 0971), one per company, closed when no Open request is left. */
export const CHANGE_REQUEST_REVIEW_KIND = 'ChangeRequestReview';

/** The Company page's tile the requests draw on (piece 13; decision 0971). */
export const CHANGE_REQUESTS_TILE = 'changeRequests';

export function isChangeRequestField(value: unknown): value is ChangeRequestField {
  return typeof value === 'string' && (CHANGE_REQUEST_FIELDS as readonly string[]).includes(value);
}

/** The field's label as the Company form itself names it, so the request and the form say the same word. */
export function changeRequestFieldLabel(field: string | null | undefined): string | null {
  if (!field) return null;
  return EDIT_FIELDS.CompanyRecord.find((row) => row.key === field)?.label ?? null;
}

/** What is wrong with a request before it is filed, or null where nothing is. */
export function changeRequestProblem(input: { requestText?: unknown; field?: unknown }): string | null {
  const text = typeof input.requestText === 'string' ? input.requestText.trim() : '';
  if (text === '') return 'Say what needs to change.';
  if (text.length > CHANGE_REQUEST_TEXT_MAX) return `Keep the request to ${CHANGE_REQUEST_TEXT_MAX.toLocaleString('en-US')} characters or fewer.`;
  if (input.field !== undefined && input.field !== null && input.field !== '' && !isChangeRequestField(input.field)) return 'That field cannot be named on a change request. Leave the field empty and say it in the request.';
  return null;
}

/** 🔒 A decline carries a note, because the note is what the requester reads beside the status. */
export function declineProblem(note: unknown): string | null {
  const text = typeof note === 'string' ? note.trim() : '';
  if (text === '') return 'Say why the request is declined. The person who filed it reads this note.';
  if (text.length > CHANGE_REQUEST_TEXT_MAX) return `Keep the note to ${CHANGE_REQUEST_TEXT_MAX.toLocaleString('en-US')} characters or fewer.`;
  return null;
}

/** 🔨 A request is decided once: a second decision is refused in these words rather than overwriting the first. */
export const CHANGE_REQUEST_DECIDED = 'This request has already been decided.';

/** One request as the Change requests tile draws it: the tile row's `raw` (decision 0971). */
export interface ChangeRequestView {
  id: string;
  field: string | null;
  fieldLabel: string | null;
  requestText: string;
  status: ChangeRequestStatus;
  requestedBy: string;
  createdAt: string;
  decidedBy: string | null;
  decidedAt: string | null;
  decisionNote: string | null;
  /** 🔒 The server's answer: this viewer may Accept and Decline this row now. */
  decidable: boolean;
}
